/**
 * Module: web/src/stores/reviewIncentives.ts
 * Author: Salvo Praxis (w/ AI pair programming partner Claude Code by Anthropic)
 * Version: 1.3.0
 * Date: 2026-07-14
 *
 * v1.3.0 (P9.6 queue filter row, contract v1.7.0 -- decisions #168/#169):
 * load()/loadMore() gain an optional ReviewListFilters {person_id, from, to}
 * rider wired to the list endpoint's existing params -- counts come back
 * FILTERED (the v1.0.1 rule: counts honor person/date, ignore state), so
 * the state chips stay truthful under a person/date filter. loadDrivers
 * normalizes the additive v1.7.0 `waived` counter (0 when absent).
 *
 * v1.2.0 (RI nav badge, OPERATIONS-S9): refreshCounts() -- a page_size-1
 * pending fetch just for the counts envelope, seeded by AppShell on mount
 * (assistedQueue.refreshCount parity) so the Review Incentives nav item can
 * carry the pending-count chip. Action freshness costs nothing extra:
 * attribute/skip/reopen already adjust `counts` locally and the badge reads
 * this same store.
 *
 * v1.1.0 (P9.4, contract v1.4.0): loadDrivers normalizes the additive
 * suggested_pending counter (unconfirmed AI suggestions; 0 when the API
 * predates the field).
 *
 * Review Incentives store (P9, contract review-incentives-api v1.0.0):
 * the review queue list + state counts + the driver roster, shared by the
 * queue view and the reports view so both render off one cache.
 *
 * Freshness is action-driven (assistedQueue pattern): actioned reviews are
 * patched IN PLACE from a detail re-fetch (the card stays visible with its
 * new state chip -- the office manager sees the transition land) and the
 * counts are adjusted locally from the exact state delta; a filter change or
 * Refresh re-syncs from the server.
 */
import { defineStore } from 'pinia';
import { ref } from 'vue';
import { apiGet, unwrapList } from '@/composables/useApi';
import type {
  ReviewAttributionState,
  ReviewDriver,
  ReviewIncentiveDetail,
  ReviewListItem,
  ReviewListResponse,
} from '@/types/api';

export type ReviewStateFilter = ReviewAttributionState | 'all';

/**
 * Person/date filter rider for the queue list (contract v1.7.0 §3 --
 * decision #169: person_id matches confirmed mentions OR, on pending
 * reviews, AI suggestions for the person). Empty/undefined members are
 * simply not sent.
 */
export interface ReviewListFilters {
  person_id?: number | null;
  from?: string; // YYYY-MM-DD
  to?: string;   // YYYY-MM-DD
}

function applyFilters(qs: URLSearchParams, filters?: ReviewListFilters): void {
  if (!filters) return;
  if (filters.person_id) qs.set('person_id', String(filters.person_id));
  if (filters.from) qs.set('from', filters.from);
  if (filters.to) qs.set('to', filters.to);
}

/** Page size the queue requests (legacy volume ~130 rows total -- Load more appends). */
export const REVIEW_PAGE_SIZE = 25;

export const useReviewIncentivesStore = defineStore('reviewIncentives', () => {
  const reviews = ref<ReviewListItem[]>([]);
  const total = ref(0);
  const counts = ref<{ pending: number; attributed: number; skipped: number } | null>(null);
  const page = ref(1);
  const loading = ref(false);
  const loadingMore = ref(false);
  const errorMessage = ref('');

  const drivers = ref<ReviewDriver[]>([]);
  const driversError = ref('');

  /** Load page 1 for one state filter (replaces the list). v1.3.0: optional
   *  person/date filters ride the same request; counts return filtered. */
  async function load(state: ReviewStateFilter, filters?: ReviewListFilters): Promise<void> {
    loading.value = true;
    errorMessage.value = '';
    try {
      const qs = new URLSearchParams({
        state,
        page: '1',
        page_size: String(REVIEW_PAGE_SIZE),
      });
      applyFilters(qs, filters);
      const resp = await apiGet<ReviewListResponse>('/review-incentives/reviews', qs.toString());
      reviews.value = resp.reviews ?? [];
      total.value = resp.total ?? reviews.value.length;
      counts.value = resp.counts ?? null;
      page.value = 1;
    } catch {
      errorMessage.value = 'Could not load the review queue. Try again.';
    } finally {
      loading.value = false;
    }
  }

  /** Append the next page (same filters the caller passed to load). */
  async function loadMore(state: ReviewStateFilter, filters?: ReviewListFilters): Promise<void> {
    if (loadingMore.value || reviews.value.length >= total.value) return;
    loadingMore.value = true;
    try {
      const qs = new URLSearchParams({
        state,
        page: String(page.value + 1),
        page_size: String(REVIEW_PAGE_SIZE),
      });
      applyFilters(qs, filters);
      const resp = await apiGet<ReviewListResponse>('/review-incentives/reviews', qs.toString());
      const known = new Set(reviews.value.map((r) => r.id));
      reviews.value.push(...(resp.reviews ?? []).filter((r) => !known.has(r.id)));
      total.value = resp.total ?? total.value;
      counts.value = resp.counts ?? counts.value;
      page.value += 1;
    } catch {
      errorMessage.value = 'Could not load more reviews. Try again.';
    } finally {
      loadingMore.value = false;
    }
  }

  /** Badge-only refresh (AppShell mount -- the RI nav chip). */
  async function refreshCounts(): Promise<void> {
    try {
      const qs = new URLSearchParams({ state: 'pending', page: '1', page_size: '1' });
      const resp = await apiGet<ReviewListResponse>('/review-incentives/reviews', qs.toString());
      counts.value = resp.counts ?? counts.value;
    } catch {
      /* badge is cosmetic -- a failed refresh keeps the last known value */
    }
  }

  /**
   * Patch one review card in place after an action (detail re-fetch result).
   * Adjusts the state counts from the exact old->new transition so the filter
   * chips stay truthful without a full list round-trip.
   */
  function patchReview(detail: ReviewIncentiveDetail): void {
    const idx = reviews.value.findIndex((r) => r.id === detail.id);
    if (idx < 0) return;
    const oldState = reviews.value[idx].attribution_state;
    const newState = detail.attribution_state;
    reviews.value[idx] = {
      ...reviews.value[idx],
      attribution_state: detail.attribution_state,
      mentions: detail.mentions,
      // excerpt/date/location are immutable server-side -- keep the list copy
    };
    if (counts.value && oldState !== newState) {
      counts.value = {
        ...counts.value,
        [oldState]: Math.max(0, counts.value[oldState] - 1),
        [newState]: counts.value[newState] + 1,
      };
    }
  }

  /**
   * Driver roster (GET /drivers -- active-first, per-person counters).
   * Tolerant unwrap: {drivers:[...]} pinned loosely by the contract; accept
   * bare array / {people:[...]} too, and normalize person_id vs id.
   */
  async function loadDrivers(): Promise<void> {
    driversError.value = '';
    try {
      const body = await apiGet<unknown>('/review-incentives/drivers');
      const rows = unwrapList<Record<string, unknown>>(body, ['drivers', 'people']);
      drivers.value = rows.map((r) => ({
        person_id: Number(r.person_id ?? r.id),
        full_name: String(r.full_name ?? r.name ?? ''),
        active: r.active === undefined ? true : Boolean(r.active),
        mentions: Number(r.mentions ?? 0),
        paid: Number(r.paid ?? 0),
        unpaid: Number(r.unpaid ?? 0),
        // v1.3.0 (contract v1.7.0): waived-mention count; 0 pre-v1.7 APIs.
        waived: Number(r.waived ?? 0),
        last_mention_at: (r.last_mention_at as string | null) ?? null,
        suggested_pending: Number(r.suggested_pending ?? 0),
      }));
    } catch {
      driversError.value = 'Could not load the driver roster.';
    }
  }

  return {
    reviews,
    total,
    counts,
    page,
    loading,
    loadingMore,
    errorMessage,
    drivers,
    driversError,
    load,
    loadMore,
    refreshCounts,
    patchReview,
    loadDrivers,
  };
});
